import styled from "@emotion/styled"
import { Button, Typography } from "@mui/material"
import BouncingImage from "common/BouncingImage"
import { GlobalContext } from "common/GlobalContext"
import { Desktop, Mobile, useIsDesktop } from "common/MediaQueries"
import { SectionProps } from "common/SectionProps"
import React, { useContext } from "react"
import {
  bouncingWrapper,
  offScreen,
  SectionContainer,
  transition,
} from "theme/shared"

const Styled = {
  SectionContainer: styled(SectionContainer)`
    padding: 80px 0 140px;
    justify-content: flex-start;

    /* Override parent's transform reset for the Services List */
    > ul {
      ${transition}
      transform: translateY(-50%) !important;
      ${(p) =>
        p.active &&
        `
        visibility: visible !important;
        opacity: 1 !important;
    `}
    }

    li {
      ${transition}
      ${(p) =>
        p.active &&
        `
        transform: translate3d(0,0,0);
        visibility:visible !important;
        opacity:1 !important;
    `}
    }

    ${(p) => p.theme.breakpoints.up("md")} {
      padding: 150px 160px 0;

      @media (max-height: 650px) {
        padding-top: 100px;
      }
    }
  `,
  Headline: styled(Typography)`
    ${offScreen({ xPos: "calc(-100% - 80vh)" })}
    span {
      font-weight: 500;
    }
    ${(p) => p.theme.breakpoints.up("md")} {
      font-size: 60px;
      line-height: 36px;

      @media (max-height: 650px) {
        font-size: 45px;
      }
    }
  `,
  List: styled.ul`
    list-style: none;
    position: absolute;
    top: 50%;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    justify-content: center;

    ${(p) => p.theme.breakpoints.up("md")} {
      flex-direction: initial;
      flex-wrap: wrap;
    }
  `,
  Item: styled.li<{ factor: number; shift: number; listNumber?: number }>`
    padding: 0;
    margin: 0;
    font-size: 16px;
    display: flex;
    justify-content: center;
    :not(:last-child) {
      margin-bottom: 20px;
    }
    margin-left: ${(p) => p.shift}px;
    ${(p) => offScreen({ xPos: `calc(-50% - 50vh * ${p.factor})` })}

    ${(p) => p.theme.breakpoints.up("md")} {
      width: 100%;
      align-items: stretch;
      flex-grow: 1;
    }
  `,
  Floating: styled(bouncingWrapper)`
    position: relative;
    display: flex;
    align-items: center;
    margin-right: 10px;
  `,
  Button: styled(Button)`
    ${offScreen({ xPos: "calc(-50% - 100vh)" })}
    margin-top: 50px;
    height: 48px;
    width: 242px;
    padding: 0;
    ${(p) => p.theme.breakpoints.down("md")} {
      display: none;
    }
  `,

  Feature: styled(Typography)`
    font-weight: bold;
    font-size: 16px;
    letter-spacing: normal;

    ${(p) => p.theme.breakpoints.up("md")} {
      font-size: 20px;
    }
  `,

  FeatureBranch: styled(Typography)<{ component?: string }>`
    font-weight: 500;
    font-size: 16px;
    letter-spacing: normal;
    ${(p) => p.theme.breakpoints.up("md")} {
      font-size: 20px;
    }
  `,
}

interface Service {
  id: number
  name: string
  nameBold: string
  icon: string
  iconWidth: number
  iconHeight: number
  order: number
}

interface SectionServicesProps extends SectionProps {
  section?: {
    titlePrefix?: string
    titleSuffix?: string
    sectionServices?: Service[]
  }
}

// Default services as fallback (hardcoded current values)
const defaultServices: Service[] = [
  {
    id: 1,
    name: "Native & React Native App",
    nameBold: "Development",
    icon: "./blue_circle.svg",
    iconWidth: 80.6,
    iconHeight: 80.6,
    order: 0,
  },
  {
    id: 2,
    name: "Website",
    nameBold: "Development",
    icon: "./services/image02.svg",
    iconWidth: 72.5,
    iconHeight: 74.8,
    order: 1,
  },
  {
    id: 3,
    name: "Design Thinking & Product Strategy",
    nameBold: "",
    icon: "./services/image03.svg",
    iconWidth: 68.5,
    iconHeight: 59.3,
    order: 2,
  },
  {
    id: 4,
    name: "UI & UX",
    nameBold: "",
    icon: "./services/image04.svg",
    iconWidth: 72,
    iconHeight: 73.9,
    order: 3,
  },
  {
    id: 5,
    name: "Product",
    nameBold: "Management",
    icon: "./services/image05.svg",
    iconWidth: 73.1,
    iconHeight: 63.2,
    order: 4,
  },
  {
    id: 6,
    name: "Systems Design",
    nameBold: "Development",
    icon: "./services/image06.svg",
    iconWidth: 71.1,
    iconHeight: 67.5,
    order: 5,
  },
]

const SectionServices = ({ position, section }: SectionServicesProps) => {
  const { active, openMenu, prev, next } = useContext(GlobalContext)
  const isDesktop = useIsDesktop()

  // Use database content with fallbacks
  const titlePrefix = section?.titlePrefix || "Our"
  const titleSuffix = section?.titleSuffix || "Services"
  const services = section?.sectionServices || defaultServices

  return (
    <Styled.SectionContainer
      active={active === position}
      openMenu={openMenu}
      data-section="services"
    >
      <Styled.Headline variant="h2">
        <span>{titlePrefix}</span> {titleSuffix}
      </Styled.Headline>
      <Desktop>
        <Styled.List>
          {(() => {
            const rows: React.ReactElement[] = []
            for (let i = 0; i < services.length; i += 2) {
              const rowServices = services.slice(i, i + 2)
              rows.push(
                <Styled.Item key={`row-${i}`} factor={0} shift={0}>
                  {rowServices.map((service, idx) => {
                    const deg = idx % 2 === 0 ? 45 : -45
                    return (
                      <Styled.Floating key={service.id}>
                        <BouncingImage
                          deg={deg}
                          distance={0.3}
                          image={service.icon}
                          width={isDesktop ? `${service.iconWidth}px` : "41px"}
                          height={
                            isDesktop ? `${service.iconHeight}px` : "41px"
                          }
                        />
                        <Styled.Feature variant="h3">
                          {service.name}
                          {service.nameBold && (
                            <>
                              {" "}
                              <Styled.FeatureBranch component="span">
                                {service.nameBold}
                              </Styled.FeatureBranch>
                            </>
                          )}
                        </Styled.Feature>
                      </Styled.Floating>
                    )
                  })}
                </Styled.Item>
              )
            }
            return rows
          })()}
        </Styled.List>
      </Desktop>
      <Mobile>
        <Styled.List>
          {services.map((service, index) => {
            const deg = index % 2 === 0 ? 45 : -45
            return (
              <Styled.Item key={service.id} factor={0} shift={0}>
                <Styled.Floating>
                  <BouncingImage
                    deg={deg}
                    distance={0.3}
                    image={service.icon}
                    width={isDesktop ? `${service.iconWidth}px` : "41px"}
                    height={isDesktop ? `${service.iconHeight}px` : "41px"}
                  />
                  <Styled.Feature variant="h3">
                    {service.name}
                    {service.nameBold && (
                      <>
                        {" "}
                        <Styled.FeatureBranch component="span">
                          {service.nameBold}
                        </Styled.FeatureBranch>
                      </>
                    )}
                  </Styled.Feature>
                </Styled.Floating>
              </Styled.Item>
            )
          })}
        </Styled.List>
      </Mobile>
    </Styled.SectionContainer>
  )
}

export default SectionServices
